import { cn } from "~/lib/utils";
import { Checkbox } from "../ui/checkbox";

interface Props {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
    description?: string;
    disabled?: boolean;
    className?: string;
    labelClassName?: string;
}

/**
 * A generic toggle component for table filtering
 */
export function TableToggle(props: Props) {
    const {
        checked,
        onChange,
        label,
        description = null,
        disabled = false,
        className,
        labelClassName,
    } = props;

    const handleToggle = () => {
        if (disabled) return;
        onChange(!checked);
    };

    return (
        <div
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={(event) => {
                if (disabled) return;

                if (event.key === "Enter" || event.key === " ") {
                    handleToggle();
                }
            }}
            className={cn(
                "flex flex-col cursor-pointer overflow-hidden group select-none",
                disabled && "opacity-50 cursor-not-allowed",
                className,
            )}
            aria-label={label}
        >
            <div className="flex items-center gap-2">
                <Checkbox
                    checked={checked}
                    onChange={handleToggle}
                    className="transition-colors border-muted-foreground group-hover:border-primary"
                    disabled={disabled}
                />
                <span
                    className={cn(
                        "text-secondary-foreground whitespace-nowrap transition-colors",
                        {
                            "text-xs": description !== null,
                            "text-sm": description === null,
                            "group-hover:text-foreground": !disabled,
                        },
                        "leading-0", // NOTE - this is included after text-* to override the default leading-normal they implement
                        labelClassName,
                    )}
                >
                    {label}
                </span>
            </div>
            {description && (
                <p
                    className={cn(
                        "text-muted-foreground text-xs whitespace-nowrap transition-colors",
                        {
                            "group-hover:text-slate-700": !disabled,
                        },
                    )}
                >
                    {description}
                </p>
            )}
        </div>
    );
}
